import { useMemo } from "react";
import AzureFieldMappingsView from "./AzureFieldMappingsView";
import { useAuthContext } from "../../../../../context/AuthContext";
import { useLanguageContext } from "../../../../../context/LanguageContext";

function AzureFieldMappings({
  configData,
  setConfigData,
  departments,
  formId,
}) {
  const authContext = useAuthContext();
  const languageContext = useLanguageContext();

  /**
   * Create a list of the available entities that can be mapped
   */
  const entities = useMemo(() => {
    return ["user", "employee"];
  }, []);

  /**
   * Create a list of the available azure fields that can be mapped.
   */
  const azureFields = useMemo(() => {
    return [
      "displayName",
      "givenName",
      "surname",
      "jobTitle",
      "mail",
      "mobilePhone",
      "officeLocation",
      "preferredLanguage",
      "userPrincipalName",
    ];
  }, []);

  /**
   * Create a mapping of the available fields for each entity that can be mapped.
   */
  const fields = useMemo(() => {
    return {
      user: ["name", "location_id", "language_id"],
      employee: [
        "location_id",
        "department_id",
        "title",
        "first_name",
        "last_name",
        "phone_number",
        "license_plate",
      ],
    };
  }, []);

  /**
   * Create a mapping of the options for each field that can be mapped.
   */
  const fieldValues = useMemo(() => {
    return {
      location_id: authContext.auth?.user?.company?.locations?.map(
        (location) => ({
          value: location.id,
          label: location.name,
        }),
      ),
      language_id: languageContext.activeLanguages.map((language) => ({
        value: language.id,
        label: language.label,
      })),
      department_id: departments.map((department) => ({
        value: department.id,
        label: department.name,
      })),
    };
  }, [
    authContext.auth?.user?.company?.locations,
    languageContext.activeLanguages,
    departments,
  ]);

  /**
   * Create a mapping of the available fields for each entity by filtering out the already mapped fields from the config data
   */
  const availableFields = useMemo(() => {
    return Object.keys(fields).reduce((acc, entity) => {
      acc[entity] = fields[entity].filter(
        (field) =>
          !configData.field_mappings.some(
            (mapping) => mapping.entity === entity && mapping.field === field,
          ),
      );
      return acc;
    }, {});
  }, [configData.field_mappings, fields]);

  /**
   * Create a mapping of the field mappings for easier access in the view
   */
  const fieldMappings = useMemo(() => {
    return entities.reduce((acc, entity) => {
      acc[entity] = configData.field_mappings
        .filter((mapping) => mapping.entity === entity)
        .map((fieldMapping) => ({
          ...fieldMapping,
          value_mappings: fieldMapping.value_mappings.map((valueMapping) => ({
            ...valueMapping,
            label:
              (fieldValues[fieldMapping.field] ?? []).find(
                (fieldValue) =>
                  fieldValue.value === valueMapping[fieldMapping.field],
              )?.label || valueMapping[fieldMapping.field],
          })),
          availableValues: (fieldValues[fieldMapping.field] ?? []).filter(
            (fieldValue) =>
              !fieldMapping.value_mappings.some(
                (vm) => vm[fieldMapping.field] === fieldValue.value,
              ),
          ),
        }));
      return acc;
    }, {});
  }, [configData.field_mappings, entities, fieldValues]);

  /**
   * This method will handle the addition of a new field mapping
   * @param {string} entity
   * @param {string} field
   */
  function onFieldMappingAdd(entity, field) {
    setConfigData((prev) => ({
      ...prev,
      field_mappings: [
        ...prev.field_mappings,
        { entity, field, azure_field: azureFields[0], value_mappings: [] },
      ],
    }));
  }

  /**
   * This method will handle the change of an azure field in the mapping
   * @param {string} entity
   * @param {string} field
   * @param {string} azure_field
   */
  function onAzureFieldChange(entity, field, azure_field) {
    setConfigData((prev) => ({
      ...prev,
      field_mappings: prev.field_mappings.map((mapping) =>
        mapping.entity === entity && mapping.field === field
          ? { ...mapping, azure_field }
          : mapping,
      ),
    }));
  }

  /**
   * This method will handle the deletion of a field mapping
   * @param {string} entity
   * @param {string} field
   */
  function onFieldMappingDelete(entity, field) {
    setConfigData((prev) => {
      return {
        ...prev,
        field_mappings: prev.field_mappings.filter(
          (mapping) => !(mapping.entity === entity && mapping.field === field),
        ),
      };
    });
  }

  /**
   * This method will handle the addition of a new value to a field mapping
   * @param {string} field
   * @param {string} value
   */
  function onFieldValueMappingAdd(entity, field, value) {
    setConfigData((prev) => ({
      ...prev,
      field_mappings: prev.field_mappings.map((mapping) => {
        if (mapping.entity === entity && mapping.field === field) {
          return {
            ...mapping,
            value_mappings: [
              ...mapping.value_mappings,
              {
                azure_value: "Azure value",
                [field]: value,
              },
            ],
          };
        }
        return mapping;
      }),
    }));
  }

  /**
   * This method will handle the change of an azure value in the field value mapping
   * @param {string} entity
   * @param {string} field
   * @param {string} fieldValue
   * @param {string} azureValue
   */
  function onAzureValueChange(entity, field, fieldValue, azureValue) {
    setConfigData((prev) => ({
      ...prev,
      field_mappings: prev.field_mappings.map((mapping) => {
        if (mapping.entity === entity && mapping.field === field) {
          return {
            ...mapping,
            value_mappings: mapping.value_mappings.map((valueMapping) =>
              valueMapping[field] === fieldValue
                ? { ...valueMapping, azure_value: azureValue }
                : valueMapping,
            ),
          };
        }
        return mapping;
      }),
    }));
  }

  /**
   * This method will handle the deletion of a field value mapping
   * @param {string} entity
   * @param {string} field
   * @param {string} fieldValue
   */
  function onFieldValueMappingDelete(entity, field, fieldValue) {
    setConfigData((prev) => ({
      ...prev,
      field_mappings: prev.field_mappings.map((mapping) => {
        if (mapping.entity === entity && mapping.field === field) {
          return {
            ...mapping,
            value_mappings: mapping.value_mappings.filter(
              (valueMapping) => valueMapping[field] !== fieldValue,
            ),
          };
        }
        return mapping;
      }),
    }));
  }

  return (
    <AzureFieldMappingsView
      availableEntities={entities}
      availableAzureFields={azureFields}
      availableFields={availableFields}
      fieldMappings={fieldMappings}
      onFieldMappingAdd={onFieldMappingAdd}
      onAzureFieldChange={onAzureFieldChange}
      onAzureValueChange={onAzureValueChange}
      onFieldMappingDelete={onFieldMappingDelete}
      onFieldValueMappingAdd={onFieldValueMappingAdd}
      onFieldValueMappingDelete={onFieldValueMappingDelete}
      formId={formId}
    />
  );
}

export default AzureFieldMappings;
